import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Keyboard,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import {
  NavigationHeader,
  Props as NavigationHeaderProps,
} from './navigation-header';
import { SharedValue, useSharedValue } from 'react-native-reanimated';
import { useIndexScrollbarContext } from '@/contexts/index-scrollbar-context';
import { useLimitedRenderPage } from '@/hooks/use-limited-performance';
import { useStatusBarColor } from '@/hooks/use-status-bar-color';
import { useTheme } from '@/contexts/theme-context';

interface BaseProps {
  containerStyle?: StyleProp<ViewStyle>;
  loading?: boolean;
  navigationHeaderProps?: NavigationHeaderProps;
  shouldDismissKeyboard?: boolean;
  statusBarColor?: string;
}

export type AnimatedProps = BaseProps & {
  animateNavigationHeader: true;
  children: (
    animatedScrollViewProps: ScrollViewProps,
    scrollY: SharedValue<number>,
  ) => React.ReactNode;
  showNavigationHeader?: true;
};

export type NonAnimatedProps = BaseProps & {
  animateNavigationHeader?: false;
  children?: React.ReactNode;
  showNavigationHeader?: false;
};

export type Props = AnimatedProps | NonAnimatedProps;

export interface PageContainerScrollRef {
  resetScrollPosition: () => void;
}

export const PageContainer = React.forwardRef<PageContainerScrollRef, Props>(
  (
    {
      containerStyle,
      loading,
      navigationHeaderProps = {},
      shouldDismissKeyboard,
      showNavigationHeader = true,
      statusBarColor,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const indexScrollbarContext = useIndexScrollbarContext();
    const shouldLazilyRenderPage = useLimitedRenderPage();

    const scrollY = useSharedValue(0);
    const maxScrollValueRef = useRef(0);
    const [scrollViewProps, setScrollViewProps] = useState<ScrollViewProps>({});

    useStatusBarColor(statusBarColor || theme.colors.white);

    useEffect(() => {
      // Reset animated header position whenever loading state changes since the
      // list scroll position resets and may leave unnecessary whitespace below
      // the header
      scrollY.value = 0;
    }, [loading, scrollY]);

    useImperativeHandle(ref, () => ({
      resetScrollPosition: () => {
        scrollY.value = 0;
      },
    }));

    const onHeaderLayout = useCallback(
      (e: LayoutChangeEvent) => {
        setScrollViewProps({
          contentContainerStyle: {
            paddingTop: e.nativeEvent.layout.height,
          },
          onScroll: (scrollEvent: NativeSyntheticEvent<NativeScrollEvent>) => {
            const { contentOffset, contentSize, layoutMeasurement } =
              scrollEvent.nativeEvent;
            // Clamp the scroll value to prevent values outside of the list
            // range from causing the header to bounce along with the list
            const max = contentSize.height - layoutMeasurement.height;
            maxScrollValueRef.current = max > 0 ? max : 0;
            scrollY.value = Math.max(
              0,
              Math.min(contentOffset.y, maxScrollValueRef.current),
            );
            indexScrollbarContext?.onScrollViewScroll(scrollEvent);
          },
          scrollEventThrottle: 16,
        });
      },
      [scrollY, indexScrollbarContext],
    );

    // Navigation header animation is disabled on web but `children` is always a
    // function when `animateNavigationHeader` is true
    const children = rest.animateNavigationHeader
      ? rest.children(scrollViewProps, scrollY)
      : rest.children;
    const content = (
      <View style={[styles.container, containerStyle]}>
        <View style={styles.content}>{children}</View>
        {showNavigationHeader ? (
          <NavigationHeader
            animate={rest.animateNavigationHeader && Platform.OS !== 'web'}
            onLayout={onHeaderLayout}
            scrollY={scrollY}
            {...navigationHeaderProps}
          />
        ) : null}
      </View>
    );

    if (!shouldLazilyRenderPage) {
      return null;
    }

    return shouldDismissKeyboard && Platform.OS !== 'web' ? (
      <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
        {content}
      </TouchableWithoutFeedback>
    ) : (
      content
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column-reverse',
  },
  content: {
    flex: 1,
  },
});
